import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, TrendingUp, Rocket, Landmark, Flame, Cpu, Brain, Eye, ArrowRight, Volume2, VolumeX } from "lucide-react";
import { FaYoutube, FaTiktok, FaInstagram } from "react-icons/fa";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api from "@/lib/api";

const PLATFORM_ICONS = { YouTube: FaYoutube, TikTok: FaTiktok, Instagram: FaInstagram };

function FeaturedReel() {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const v = videoRef.current;
    if (v) {
      v.muted = true;  // React doesn't always render the muted attribute — set it, or Chrome blocks autoplay
      v.play().catch(() => {});
    }
  }, []);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (v.paused) v.play().catch(() => {});
  };

  return (
    <div className="mb-14" data-testid="featured-reel">
      <div className="relative rounded-lg overflow-hidden border border-white/10 shadow-[0_20px_60px_rgba(0,0,0,0.5)] max-w-3xl">
        <video
          ref={videoRef}
          poster="/demo-reel-poster.jpg"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="featured-reel-video"
          className="w-full aspect-video object-cover bg-black"
        >
          <source src="/demo-reel.webm" type="video/webm" />
          <source src="/demo-reel.mp4" type="video/mp4" />
        </video>
        <span className="absolute left-3 top-3 text-[10px] uppercase tracking-wider bg-blue-600/90 backdrop-blur px-2.5 py-1 rounded-full text-white font-semibold">
          Real render · made 100% in Videoileo
        </span>
        <button
          onClick={toggleSound}
          data-testid="featured-reel-sound-btn"
          title={muted ? "Unmute — hear the AI voice & soundtrack" : "Mute"}
          className="absolute bottom-3 right-3 h-11 w-11 rounded-full bg-black/60 backdrop-blur border border-white/20 hover:bg-blue-600 flex items-center justify-center transition-colors"
        >
          {muted ? <VolumeX className="h-5 w-5 text-white" /> : <Volume2 className="h-5 w-5 text-white" />}
        </button>
      </div>
      <p className="mt-3 text-sm text-zinc-500 max-w-3xl">
        AI script · studio voiceover · comic-book visuals · soundtrack · <span className="text-zinc-300">cinematic 3D motion &amp; crossfades</span> — start to finish, zero editing. Tap the speaker to hear it.
      </p>
    </div>
  );
}

const EXAMPLES = [
  { title: "5 Money Habits That Quietly Build Wealth", niche: "Finance", platform: "YouTube", duration: "6:12", views: "412K", hook: "Most people never get rich because of one habit they repeat every single morning…", icon: TrendingUp, hue: "from-emerald-900/50" },
  { title: "The Universe Is Weirder Than You Think", niche: "Science", platform: "YouTube", duration: "7:48", views: "1.2M", hook: "What if everything you were taught about reality was only half the story?", icon: Rocket, hue: "from-indigo-900/50" },
  { title: "The Empire That Vanished Overnight", niche: "History", platform: "TikTok", duration: "0:58", views: "880K", hook: "One night, an entire civilization simply disappeared. Here's what really happened.", icon: Landmark, hue: "from-amber-900/50" },
  { title: "Do This Every Morning to Win the Day", niche: "Motivation", platform: "Instagram", duration: "0:44", views: "655K", hook: "The first 20 minutes after you wake up decide everything. Here's the routine.", icon: Flame, hue: "from-rose-900/50" },
  { title: "The AI Tool Quietly Replacing Jobs", niche: "Tech & AI", platform: "YouTube", duration: "5:30", views: "740K", hook: "While everyone argued online, this tool quietly automated an entire industry.", icon: Cpu, hue: "from-sky-900/50" },
  { title: "Your Brain on Dopamine, Explained", niche: "Psychology", platform: "TikTok", duration: "1:02", views: "2.1M", hook: "You're not lazy — your brain is just chasing the wrong kind of reward.", icon: Brain, hue: "from-fuchsia-900/50" },
];

export default function Examples() {
  const [active, setActive] = useState(null);
  const [voices, setVoices] = useState([]);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    api.get("/voices/featured").then((r) => setVoices(r.data.voices || [])).catch(() => {});
  }, []);

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlaying(false);
  };

  const activeVoice = active && voices.length ? voices[active._i % voices.length] : null;

  const toggleVoice = () => {
    if (!activeVoice?.preview_url) return;
    if (playing) {
      stopAudio();
      return;
    }
    const audio = new Audio(activeVoice.preview_url);
    audio.onended = () => setPlaying(false);
    audio.onerror = () => setPlaying(false);
    audioRef.current = audio;
    audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  const closeDialog = () => {
    stopAudio();
    setActive(null);
  };

  return (
    <section id="examples" className="py-24 px-5 border-t border-white/10">
      <div className="max-w-7xl mx-auto">
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-medium mb-3">Examples</p>
        <h2 className="font-head text-3xl md:text-5xl tracking-tight font-bold max-w-2xl mb-3">Faceless videos, ready to post.</h2>
        <p className="text-zinc-400 mb-12 max-w-xl leading-relaxed">
          A peek at the kind of long-form and short-form content creators ship with Videoileo — script, voiceover and
          cinematic visuals, all generated. Open any one to <span className="text-zinc-200">hear a real AI voice</span>.
        </p>

        <FeaturedReel />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {EXAMPLES.map((ex, i) => {
            const P = PLATFORM_ICONS[ex.platform];
            return (
              <button key={ex.title} onClick={() => setActive({ ...ex, _i: i })} data-testid={`example-${i}`} className="group text-left">
                <div className={`relative aspect-video rounded-md overflow-hidden border border-white/10 bg-gradient-to-br ${ex.hue} to-[#0A0A0A]`}>
                  <div className="absolute inset-0 grid-noise" />
                  <ex.icon className="absolute right-4 top-4 h-7 w-7 text-white/15" />
                  <span className="absolute left-3 top-3 text-[10px] uppercase tracking-wider bg-black/50 backdrop-blur px-2 py-0.5 rounded-full text-zinc-300">Example</span>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="h-12 w-12 rounded-full bg-blue-600/90 flex items-center justify-center transition-transform duration-200 group-hover:scale-110">
                      <Play className="h-5 w-5 text-white ml-0.5" fill="white" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/80 to-transparent">
                    <div className="flex items-center justify-between text-[11px] text-zinc-300">
                      <span className="flex items-center gap-1.5"><P className="h-3.5 w-3.5" /> {ex.niche}</span>
                      <span className="flex items-center gap-2">
                        <span className="flex items-center gap-1"><Eye className="h-3 w-3" />{ex.views}</span>
                        <span>{ex.duration}</span>
                      </span>
                    </div>
                  </div>
                </div>
                <p className="mt-3 font-medium text-sm group-hover:text-blue-400 transition-colors">{ex.title}</p>
              </button>
            );
          })}
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && closeDialog()}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl p-0 overflow-hidden" data-testid="example-dialog">
          {active && (
            <div>
              <DialogTitle className="sr-only">{active.title}</DialogTitle>
              <DialogDescription className="sr-only">Example {active.niche} faceless video preview with AI voice sample</DialogDescription>
              <div className={`relative aspect-video bg-gradient-to-br ${active.hue} to-[#0A0A0A] flex items-center justify-center`}>
                <div className="absolute inset-0 grid-noise" />
                <active.icon className="absolute right-5 top-5 h-10 w-10 text-white/15" />
                <div className="relative z-10 text-center px-8">
                  <p className="font-head text-2xl md:text-3xl font-black tracking-tight drop-shadow-lg">"{active.hook}"</p>
                </div>
                <button
                  onClick={toggleVoice}
                  disabled={!activeVoice?.preview_url}
                  data-testid="example-voice-play"
                  title={activeVoice ? `Hear the ${activeVoice.name} AI voice` : "Voice sample unavailable"}
                  className="absolute bottom-3 right-3 h-12 w-12 rounded-full bg-blue-600 hover:bg-blue-500 flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed pulse-ring"
                >
                  {playing ? <Pause className="h-5 w-5 text-white" fill="white" /> : <Play className="h-5 w-5 text-white ml-0.5" fill="white" />}
                </button>
              </div>
              <div className="p-6">
                <h3 className="font-head text-lg font-semibold">{active.title}</h3>
                <p className="text-sm text-zinc-500 mt-1">{active.niche} · {active.platform} · {active.duration}</p>
                {activeVoice && (
                  <button
                    onClick={toggleVoice}
                    data-testid="example-voice-sample-btn"
                    className="mt-4 inline-flex items-center gap-2 bg-white/5 border border-white/10 hover:bg-white/10 px-4 py-2 rounded-md text-sm font-medium transition-colors"
                  >
                    {playing ? <Pause className="h-4 w-4 text-blue-400" /> : <Volume2 className="h-4 w-4 text-blue-400" />}
                    {playing ? "Stop sample" : `Hear the "${activeVoice.name}" AI voice`}
                  </button>
                )}
                <p className="text-xs text-zinc-600 mt-3">Illustrative example of a Videoileo-generated faceless video{activeVoice ? `, voiced by ${activeVoice.name}` : ""}.</p>
                <Link to="/register" className="mt-5 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 px-5 py-2.5 rounded-md text-sm font-semibold transition-colors">
                  Create one like this <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
